import is from '@sindresorhus/is';
import { useCallback } from 'react';

import { useMixpanelTracking } from '../../../tracking/mixpanel/use-mixpanel-tracking';
import { useJsaTrackingContext } from './use-jsa-tracking-context';

type ExportScope = 'architecture' | 'level' | 'role';

type ExportFormat = 'excel' | 'pdf';

type TrackExportParams = {
    readonly exportFormat: ExportFormat;
    readonly exportScope: ExportScope;
    readonly levelId?: string;
    readonly roleId?: string;
    readonly versionId?: string;
};

type UseTrackExportOptions = {
    readonly architectureId?: string;
};

type UseTrackExportResult = {
    readonly trackExport: (params: TrackExportParams) => void;
};

export const useTrackExport = (options: UseTrackExportOptions): UseTrackExportResult => {
    const { architectureId = '' } = options;
    const { trackEvent } = useMixpanelTracking();
    const trackingContext = useJsaTrackingContext();

    const trackExport = useCallback(
        (params: TrackExportParams): void => {
            if (is.emptyString(architectureId)) {
                return;
            }

            trackEvent({
                name: 'job-role-exported',
                payload: {
                    architectureId,
                    editorRoles: trackingContext.editorRoles,
                    exportedByUserId: trackingContext.userId,
                    exportFormat: params.exportFormat,
                    exportScope: params.exportScope,
                    levelId: params.levelId,
                    roleId: params.roleId,
                    tenantId: trackingContext.tenantId,
                    timestamp: trackingContext.timestamp,
                    versionId: params.versionId,
                },
            });
        },
        [trackEvent, trackingContext, architectureId],
    );

    return { trackExport };
};
